import { useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiTokenDTO, CreateUserInput, UserDTO, UserRole } from "@app-factory/shared";
import { api, ApiRequestError } from "@/lib/api";
import { DataTable, type Column } from "@/design/DataTable";
import { cs } from "@/i18n/cs";
import { ThemeToggle } from "@/design/ThemeToggle";
import { useAuth } from "@/auth/auth";
import { Btn, Chip, Field, PageHeader, Panel, SectionHeader, inputClass, toneColor } from "@/design/primitives";
import { HostingServersPanel } from "./HostingServersPanel";

/**
 * Nastavení Factory + správa uživatelů (OWNER only, spec §5) a osobní API
 * tokeny. Tokeny jsou záměrně pro každého ne-klientského uživatele: konektor
 * do Claude Desktopu si musí umět zapnout každý sám, jinak by se za všechny
 * musel přihlašovat owner.
 */
export function SettingsPage() {
  const { session } = useAuth();
  const role = session?.user.role;
  // klient smí jen svoje hosting servery (vlastník serveru, na kterém běží jeho projekt)
  if (role === "CLIENT") {
    return (
      <div className="af-animate-fade">
        <PageHeader title={cs.settings.title} kicker="FACTORY" lead={cs.settings.lead} />
        <HostingServersPanel />
      </div>
    );
  }
  if (!role) {
    return (
      <Panel className="mx-auto mt-16 max-w-md p-6 text-center">
        <div className="af-label" style={{ color: "var(--critical)" }}>
          PŘÍSTUP ODEPŘEN
        </div>
        <p className="mt-2 text-[11px]" style={{ color: "var(--text-secondary)" }}>
          {cs.common.forbidden}
        </p>
      </Panel>
    );
  }
  return (
    <div className="af-animate-fade">
      <PageHeader title={cs.settings.title} kicker="FACTORY" lead={cs.settings.lead} />

      <Panel className="p-4" data-testid="settings-appearance">
        <SectionHeader label={cs.settings.appearance} />
        <div className="mt-3 flex flex-wrap items-center gap-4">
          <div className="max-w-[420px] text-[12px]" style={{ color: "var(--text-tertiary)" }}>
            {cs.settings.appearanceNote}
          </div>
          <div className="ml-auto">
            <ThemeToggle testIdPrefix="settings-theme" />
          </div>
        </div>
      </Panel>

      {role === "OWNER" ? (
        <div className="mt-4 grid grid-cols-1 items-start gap-4 xl:grid-cols-[380px_1fr]">
          <FactorySettings />
          <UsersPanel />
        </div>
      ) : null}
      <div className="mt-4">
        <HostingServersPanel />
      </div>
      <div className="mt-4">
        <ApiTokensPanel />
      </div>
    </div>
  );
}

/** Osobní API tokeny — vydání, přehled, odvolání. */
function ApiTokensPanel() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["api-tokens"], queryFn: api.apiTokens });
  const [name, setName] = useState("");
  const [days, setDays] = useState("");
  const [issued, setIssued] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.createApiToken({
        name: name.trim(),
        ...(days.trim() ? { expiresInDays: Number(days) } : {}),
      }),
    onSuccess: async (res) => {
      setIssued(res.secret);
      setName("");
      setDays("");
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ["api-tokens"] });
    },
    onError: (err) =>
      setError(err instanceof ApiRequestError ? err.message : cs.common.error),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.revokeApiToken(id),
    onSuccess: () => {
      // vydaný token zmizí i z okna „vidíš jen teď“ — jinak by na obrazovce
      // svítil tajný řetězec, který už neplatí
      setIssued(null);
      return queryClient.invalidateQueries({ queryKey: ["api-tokens"] });
    },
  });

  const tokens = query.data?.tokens ?? [];
  const fmt = (iso: string | null, fallback: string) =>
    iso ? new Date(iso).toLocaleString("cs-CZ") : fallback;

  return (
    <Panel className="p-5">
      <SectionHeader label={cs.settings.tokens} />
      <p className="mt-2 text-[12px]" style={{ color: "var(--text-secondary)" }}>
        {cs.settings.tokensNote} — {cs.settings.tokenScope}
      </p>

      {issued ? (
        <div
          className="mt-4 p-3"
          style={{ borderRadius: "var(--radius-box)", border: "1px solid var(--warning)", background: "color-mix(in srgb, var(--warning) 6%, transparent)" }}
          data-testid="issued-token"
        >
          <div className="af-label" style={{ color: "var(--warning)" }}>
            {cs.settings.tokenOnce}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <code
              className="min-w-0 flex-1 break-all text-[11px]"
              style={{ color: "var(--text-primary)" }}
            >
              {issued}
            </code>
            <Btn
              onClick={() => {
                void navigator.clipboard.writeText(issued).then(() => {
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2500);
                });
              }}
            >
              {copied ? cs.settings.tokenCopied : cs.settings.tokenCopy}
            </Btn>
          </div>
        </div>
      ) : null}

      <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-end">
        <div className="min-w-0 flex-1">
          <Field label={cs.settings.tokenName}>
            <input
              className={inputClass}
              value={name}
              placeholder={cs.settings.tokenNamePlaceholder}
              onChange={(e) => setName(e.target.value)}
              data-testid="token-name"
            />
          </Field>
        </div>
        <div className="md:w-[200px]">
          <Field label={cs.settings.tokenExpiry}>
            <input
              className={inputClass}
              value={days}
              inputMode="numeric"
              onChange={(e) => setDays(e.target.value.replace(/[^0-9]/g, ""))}
            />
          </Field>
        </div>
        <Btn
          variant="primary"
          disabled={create.isPending || name.trim().length < 2}
          onClick={() => create.mutate()}
          data-testid="issue-token"
        >
          {cs.settings.tokenIssue}
        </Btn>
      </div>
      {error ? (
        <div className="mt-2 text-[12px]" style={{ color: "var(--critical)" }}>
          {error}
        </div>
      ) : null}

      <div className="mt-5">
        <DataTable
          columns={([
            {
              key: "name",
              label: cs.settings.tokenName,
              width: "minmax(160px, 1fr)",
              primary: true,
              cell: (row) => (
                <span className="flex min-w-0 items-center gap-2">
                  <span className="truncate">{row.name}</span>
                  {row.connector ? <Chip tone="violet">{cs.settings.tokenConnector}</Chip> : null}
                </span>
              ),
            },
            {
              key: "hint",
              label: "TOKEN",
              width: "130px",
              cell: (row) => <span className="af-mono text-[11px]">afp_…{row.hint}</span>,
            },
            {
              key: "lastUsed",
              label: cs.settings.tokenLastUsed,
              width: "150px",
              cell: (row) => fmt(row.lastUsedAt, cs.settings.tokenNever),
            },
            {
              key: "expires",
              label: cs.settings.tokenExpires,
              width: "150px",
              cell: (row) => fmt(row.expiresAt, cs.settings.tokenForever),
            },
            {
              key: "revoke",
              label: "",
              width: "110px",
              align: "right",
              cell: (row) => (
                <button
                  type="button"
                  className="cursor-pointer text-[12px]"
                  style={{ color: "var(--critical)" }}
                  disabled={revoke.isPending}
                  onClick={() => revoke.mutate(row.id)}
                >
                  {cs.settings.tokenRevoke}
                </button>
              ),
            },
          ] satisfies Column<ApiTokenDTO>[])}
          rows={tokens}
          rowKey={(row) => row.id}
          rowTestId={(row) => `token-row-${row.id}`}
          stackBelow={1024}
          empty={
            <div className="mt-3 text-[12px]" style={{ color: "var(--text-secondary)" }}>
              {cs.settings.tokenEmpty}
            </div>
          }
        />
      </div>
    </Panel>
  );
}

function FactorySettings() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["settings"], queryFn: api.settings });
  const [name, setName] = useState<string | null>(null);
  const [timezone, setTimezone] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const mutation = useMutation({
    mutationFn: (settings: Record<string, string>) => api.updateSettings(settings),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["settings"] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
  });

  const current = query.data?.settings ?? {};
  const nameValue = name ?? current["factory.name"] ?? "";
  const tzValue = timezone ?? current["factory.timezone"] ?? "";

  return (
    <Panel className="p-5">
      <SectionHeader label={cs.settings.factory} />
      <div className="mt-4 flex flex-col gap-4">
        <Field label={cs.settings.factoryName}>
          <input className={inputClass} value={nameValue} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label={cs.settings.timezone}>
          <input className={inputClass} value={tzValue} onChange={(e) => setTimezone(e.target.value)} />
        </Field>
        <div className="flex items-center justify-end gap-3">
          {saved ? (
            <span className="text-[12px]" style={{ color: "var(--positive)" }}>
              ✓ uloženo
            </span>
          ) : null}
          <Btn
            variant="primary"
            disabled={mutation.isPending}
            onClick={() =>
              mutation.mutate({ "factory.name": nameValue, "factory.timezone": tzValue })
            }
          >
            {cs.common.save}
          </Btn>
        </div>
      </div>
    </Panel>
  );
}

const ROLE_OPTIONS: UserRole[] = ["OWNER", "MANAGER", "MEMBER", "CLIENT"];

function roleTone(role: UserRole) {
  if (role === "OWNER") return "amber" as const;
  if (role === "MANAGER") return "green" as const;
  if (role === "CLIENT") return "violet" as const;
  return "cyan" as const;
}

function UsersPanel() {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const query = useQuery({ queryKey: ["users"], queryFn: api.users });
  const [creating, setCreating] = useState(false);

  const disableUser = useMutation({
    mutationFn: (id: string) => api.updateUser(id, { disabled: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
  });

  const updateRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: UserRole }) => api.updateUser(id, { role }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
  });

  const users = query.data?.users ?? [];

  return (
    <Panel className="p-5">
      <SectionHeader
        label={cs.settings.users}
        right={
          <button
            type="button"
            className="cursor-pointer tracking-[0.14em]"
            style={{ color: "var(--accent)" }}
            onClick={() => setCreating(true)}
            data-testid="new-user"
          >
            {cs.settings.newUser}
          </button>
        }
      />
      <div className="mt-4">
        <DataTable
          columns={([
            { key: "name", label: cs.settings.name, width: "minmax(140px, 1fr)", primary: true, cell: (u) => u.name },
            {
              key: "email",
              label: cs.settings.email,
              width: "minmax(160px, 1fr)",
              cell: (u) => <span className="af-mono text-[11px]">{u.email}</span>,
            },
            {
              key: "role",
              label: cs.settings.role,
              width: "130px",
              cell: (u) =>
                u.id === session?.user.id ? (
                  <Chip tone={roleTone(u.role)}>{u.role}</Chip>
                ) : (
                  <select
                    className="af-mono cursor-pointer px-2 py-1 text-[11px] outline-none"
                    style={{
                      borderRadius: "var(--radius-chip)",
                      background: "var(--surface-inset)",
                      color: toneColor[roleTone(u.role)],
                      border: "1px solid var(--border-field)",
                    }}
                    value={u.role}
                    disabled={updateRole.isPending}
                    onChange={(e) => updateRole.mutate({ id: u.id, role: e.target.value as UserRole })}
                    data-testid={`user-role-${u.email}`}
                  >
                    {ROLE_OPTIONS.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                ),
            },
            {
              key: "state",
              label: cs.settings.state,
              width: "120px",
              cell: (u) => (
                <span style={{ color: u.disabled ? "var(--critical)" : "var(--positive)" }}>
                  {u.disabled ? cs.settings.disabled : cs.settings.active}
                </span>
              ),
            },
            {
              key: "action",
              label: "",
              width: "110px",
              align: "right",
              cell: (u) =>
                !u.disabled && u.id !== session?.user.id ? (
                  <button
                    type="button"
                    className="cursor-pointer text-[12px]"
                    style={{ color: "var(--critical)" }}
                    onClick={() => disableUser.mutate(u.id)}
                  >
                    {cs.settings.disable}
                  </button>
                ) : null,
            },
          ] satisfies Column<UserDTO>[])}
          rows={users}
          rowKey={(u) => u.id}
          stackBelow={1024}
        />
      </div>

      {creating ? <CreateUserModal onClose={() => setCreating(false)} /> : null}
    </Panel>
  );
}

function CreateUserModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<CreateUserInput>({
    email: "",
    name: "",
    role: "MEMBER",
    password: "",
  });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => api.createUser(form),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["users"] });
      onClose();
    },
    onError: (err) => setError(err instanceof ApiRequestError ? err.message : cs.common.error),
  });

  // portál do <body>: obsah stránky je v `af-animate-fade`, což je vlastní
  // vrstva — modál uvnitř by se nedostal nad rail ani nad sticky prvky
  return createPortal(
<div
      className="fixed inset-0 z-[90] flex items-center justify-center"
      style={{ background: "var(--scrim)" }}
      onClick={onClose}
    >
      <Panel
        brackets
        className="w-[420px] max-w-[calc(100vw-24px)] p-6"
        style={{
          borderRadius: "var(--radius-panel)",
          background: "var(--surface-overlay)",
          backdropFilter: "var(--surface-blur)",
          boxShadow: "var(--emphasis-accent)",
        }}
      >
        <div onClick={(e) => e.stopPropagation()} className="flex flex-col gap-4">
          <div className="af-label">NOVÝ UŽIVATEL</div>
          <Field label={cs.settings.name}>
            <input
              className={inputClass}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="user-name"
              autoFocus
            />
          </Field>
          <Field label={cs.settings.email}>
            <input
              className={inputClass}
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="user-email"
            />
          </Field>
          <Field label={cs.settings.role} hint={cs.settings.roleHint}>
            <select
              className={inputClass}
              style={{ background: "var(--surface-inset)" }}
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value as UserRole })}
              data-testid="user-role"
            >
              <option value="MANAGER">MANAGER</option>
              <option value="MEMBER">MEMBER</option>
              <option value="CLIENT">CLIENT</option>
            </select>
          </Field>
          <Field label={cs.settings.password} hint="Min. 10 znaků.">
            <input
              className={inputClass}
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="user-password"
            />
          </Field>
          {error ? (
            <div className="text-[12px]" style={{ color: "var(--critical)" }}>
              ⚠ {error}
            </div>
          ) : null}
          <div className="flex justify-end gap-2">
            <Btn onClick={onClose}>{cs.common.cancel}</Btn>
            <Btn
              variant="primary"
              disabled={mutation.isPending || !form.email || form.password.length < 10 || !form.name}
              onClick={() => mutation.mutate()}
              data-testid="user-create"
            >
              {cs.common.create}
            </Btn>
          </div>
        </div>
      </Panel>
    </div>,
    document.body,
  );
}
